import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider, Typography, Link,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

import { ContentContainer } from '@/pages/PreOnboarding/steps/AccessCode/AccessCodeEntry.styles';
import {
  FlexContainer,
  HeaderBox,
  Title,
} from '@/pages/PreOnboarding/steps/AccessCode/CreateAccount.styles';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { loginUser } from '@/utils/sparkActivationApi';
import { validateEmail } from '@/utils/stringValidators';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';

const schema = z.object({
  email: z.string().transform((v) => v.trim()).refine(
    (v) => validateEmail(v),
    { message: 'coverage.errors.email_error' },
  ),
});

type LoginEmailForm = z.infer<typeof schema>;

const LoginEmailInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const posthog = usePostHog();
  const [isSending, setIsSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sendError, setSendError] = useState('');

  const {
    handleSubmit, control, formState, getValues,
  } = useForm<LoginEmailForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { email: '' },
  });

  useEffect(() => {
    posthog?.capture('SparkLoginEmailScreenViewed');
    posthog?.register({ auth_method: 'email' });
    posthog?.capture('LoginEmailLanded');
  }, [posthog]);

  useEffect(() => {
    const redirectTo = params.get('redirectTo');
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }
  }, [params]);

  const sendMagicLink = async (email: string) => {
    setIsSending(true);
    setSendError('');

    try {
      await loginUser({ email, product: 'spark', ph_distinct_id: posthog?.get_distinct_id() });

      posthog?.capture('SparkLoginMagicLinkSent');
      setMagicLinkSent(true);
    } catch {
      setSendError(t('coverage.pre_onboarding.create_account.error_send_failed'));
    } finally {
      setIsSending(false);
    }
  };

  const onSubmit = async (data: LoginEmailForm) => {
    posthog?.capture('SparkLoginEmailContinueClicked');
    posthog?.capture('LoginEmailMethodClicked', {
      email: data.email,
      input: data.email,
      email_method: 'magic',
    });
    await sendMagicLink(data.email);
  };

  const handleResend = async () => {
    const email = getValues('email');
    if (email) {
      await sendMagicLink(email);
    }
  };

  return (
    <ContentContainer>
      <Header />
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.spark.login.email_title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.spark.login.email_sub_title')}
          </Typography>
        </HeaderBox>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography
            variant="body2"
            style={{ fontWeight: 600, marginBottom: 4 }}
            color="textPrimary"
          >
            {t('coverage.pre_onboarding.email_auth.email_label')}
          </Typography>

          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="email"
                autoCapitalize="none"
                inputProps={{
                  placeholder: t('coverage.pre_onboarding.email_auth.email_placeholder'),
                  autoCapitalize: 'none',
                  autoCorrect: 'off',
                  type: 'email',
                }}
                error={!!fieldState.error}
                helperText={fieldState.error ? t('coverage.errors.email_error') : undefined}
                required
              />
            )}
          />

          {magicLinkSent && (
            <div style={{
              marginTop: 8,
              marginBottom: 16,
              padding: 16,
              backgroundColor: '#f5f7fa',
              borderRadius: 8,
              fontSize: 14,
            }}
            >
              <Typography variant="body2" color="textPrimary" style={{ fontWeight: 400 }}>
                {t('coverage.spark.login.magic_link_sent_message')}
                {' '}
                <Link
                  component="button"
                  type="button"
                  color="textPrimary"
                  onClick={handleResend}
                  underline="always"
                  style={{ fontWeight: 'normal', fontSize: 'inherit' }}
                >
                  {t('coverage.spark.login.click_to_resend')}
                </Link>
              </Typography>
            </div>
          )}

          {sendError && (
            <Typography color="error" data-testid="send-error" style={{ marginTop: 8 }}>
              {sendError}
            </Typography>
          )}

          <Button
            variant="contained"
            data-testid="magic-link-button"
            type="submit"
            fullWidth
            color="primary"
            disabled={!formState.isValid || isSending}
            rounded
            style={{ borderRadius: 100, marginTop: 16 }}
          >
            {isSending
              ? t('coverage.pre_onboarding.create_account.sending')
              : t('coverage.spark.login.login_with_magic_link')}
          </Button>
        </form>
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

const LoginEmail: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <LoginEmailInner />
  </ThemeProvider>
);

export default LoginEmail;
